/**
 * Putting a clip's pose onto a skeleton — `Animation::Apply` (animation.cpp:370-721),
 * the live `simpleMethod` path only.
 *
 * Three things here are not what you would write from scratch, and all three are load-bearing:
 *
 *   - THE RENDERED POSE IS ALWAYS THE MIDPOINT of frame N and frame N+1. Gameplay passes
 *     a sub-frame offset of -1, which the original reads as "no offset given" and answers
 *     with a fixed half-and-half blend. There is no time-based sub-frame sampling at all.
 *   - The blend is a LERP of the two quaternions, not a slerp, made well-behaved by first
 *     flipping one into the other's hemisphere.
 *   - The clip's own root XY is thrown away in play, because the path was already
 *     integrated by the physics solver. Only the root's HEIGHT still comes from the clip,
 *     which is what gives a walking player his vertical bob.
 *
 * On top of that sits a transition smoother that drags each joint back toward where it was
 * last tick, strongest on the first frame of a new clip and gone by the eighth, with a hard
 * cap on how fast any one joint may rotate. Elbows and knees are allowed 20% more, ankles
 * 60% more, because they genuinely move faster than the rest of a body.
 */

import { Quaternion, Vector3 } from 'three';
import { clamp, curve, normalizedClamp } from '../../core/math';
import { makeSameNeighborhood, rotationMultipliedBy, rotationTo } from '../../core/quat';
import { samplePosition, sampleRotation, type AnimClip } from './clip';
import type { Skeleton } from '../skeleton';

const UP = new Vector3(0, 0, 1);

/** animation.cpp:585-593 — the per-joint angular speed ceiling, in radians per second. */
const BASE_MAX_ANGLE_PER_SECOND = 7.5 * Math.PI;
const JOINT_SPEED_ALLOWANCE: Readonly<Record<string, number>> = {
  left_elbow: 1.2,
  right_elbow: 1.2,
  left_knee: 1.2,
  right_knee: 1.2,
  left_ankle: 1.6,
  right_ankle: 1.6,
};

/**
 * What each joint looked like last tick.
 *
 * Deliberately NOT cleared when a clip changes — that continuity across the switch is the
 * whole point of it, and clearing it makes every transition snap.
 */
export type MovementHistory = Map<string, { orientation: Quaternion; timeDiffMs: number }>;

export function createMovementHistory(): MovementHistory {
  return new Map();
}

export interface ApplyBuffer {
  clip: AnimClip;
  frameNum: number;
  /** `startAngle + rotationSmuggleOffset` — the player's world heading, in radians. */
  orientation: number;
  /** True in normal play: the clip's own root XY has already been integrated elsewhere. */
  noPos: boolean;
  smooth: boolean;
  /** 0 means no smoothing at all, which is what a natural movement-to-movement switch uses. */
  smoothFactor: number;
  /** Real time since the last pose, clamped to 10..50 ms by the caller. */
  timeDiffMs: number;
}

const preRotation = new Quaternion();
const postRotation = new Quaternion();
const currentRotation = new Quaternion();
const scratchRotation = new Quaternion();
const prePosition = new Vector3();
const postPosition = new Vector3();

/**
 * Write one frame of a clip onto a skeleton.
 *
 * The skeleton is left in LOCAL space: the root keeps only the clip's height, and the world
 * position and heading stay on the node above it. That is exactly what the original's
 * `noPos` flag means, and it keeps the render side able to scale a player by his height
 * without scaling his position across the pitch.
 */
export function applyPose(
  skeleton: Skeleton,
  buffer: ApplyBuffer,
  history: MovementHistory,
): void {
  const { clip, frameNum } = buffer;
  const bias = 0.5;

  // ---- the root: height only ---------------------------------------------
  samplePosition(clip.root, clip.frameCount, frameNum, prePosition);
  samplePosition(clip.root, clip.frameCount, frameNum + 1, postPosition);
  const rootPosition = prePosition.multiplyScalar(1 - bias).addScaledVector(postPosition, bias);
  if (buffer.noPos) {
    rootPosition.x = 0;
    rootPosition.y = 0;
  }
  skeleton.setRootPosition(rootPosition);

  // ---- every other joint --------------------------------------------------
  const beginBias = Math.pow(curve(1 - normalizedClamp(frameNum, 0, 8), 1.0), 0.5);
  const currentBias = beginBias * buffer.smoothFactor * 0.5;

  for (const track of clip.joints) {
    const jointIndex = skeleton.names.indexOf(track.name);
    if (jointIndex < 0) continue;

    sampleRotation(track, clip.frameCount, frameNum, preRotation);
    sampleRotation(track, clip.frameCount, frameNum + 1, postRotation);
    makeSameNeighborhood(preRotation, postRotation);
    const orientation = new Quaternion(
      preRotation.x * (1 - bias) + postRotation.x * bias,
      preRotation.y * (1 - bias) + postRotation.y * bias,
      preRotation.z * (1 - bias) + postRotation.z * bias,
      preRotation.w * (1 - bias) + postRotation.w * bias,
    ).normalize();

    // The whole body's world heading is injected by pre-multiplying this one joint.
    if (track.name === 'body') {
      scratchRotation.setFromAxisAngle(UP, buffer.orientation);
      orientation.premultiply(scratchRotation).normalize();
    }

    if (buffer.smooth && buffer.timeDiffMs > 0) {
      // Where this joint actually was last tick, which is not necessarily where the
      // previous clip said it should be.
      currentRotation.copy(skeleton.localRotation[jointIndex]!);
      const entry = history.get(track.name);
      if (!entry) {
        history.set(track.name, { orientation: currentRotation.clone(), timeDiffMs: 10 });
      } else {
        makeSameNeighborhood(currentRotation, entry.orientation);
      }

      orientation.slerp(currentRotation, currentBias).normalize();

      // A hard ceiling on angular speed, tightest on the clip's first frame. Note this
      // applies even when smoothing is switched off entirely, because its strength depends
      // on the frame number and not on the smooth factor — which is why even a seamless
      // movement-to-movement switch cannot snap a limb.
      const dot = makeSameNeighborhood(orientation, currentRotation);
      const anglePerSecond = (2 * Math.acos(clamp(dot, -1, 1))) / (buffer.timeDiffMs * 0.001);
      let maxAnglePerSecond =
        BASE_MAX_ANGLE_PER_SECOND * (JOINT_SPEED_ALLOWANCE[track.name] ?? 1);
      maxAnglePerSecond *= 0.3 + 0.7 * (1 - beginBias);
      if (anglePerSecond > maxAnglePerSecond) {
        const allowFraction = maxAnglePerSecond / anglePerSecond;
        const desired = rotationTo(currentRotation, orientation, scratchRotation).normalize();
        orientation
          .copy(rotationMultipliedBy(desired, allowFraction))
          .multiply(currentRotation)
          .normalize();
      }

      const stored = history.get(track.name)!;
      stored.orientation.copy(currentRotation);
      stored.timeDiffMs = buffer.timeDiffMs;
    }

    skeleton.localRotation[jointIndex]!.copy(orientation);
  }
}
